import React, { useEffect, useState, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import {
  Loader2, ChevronRight, ChevronDown, ChevronUp, TrendingUp, Clock,
  CheckCircle, Users, User, FileText, Building2, Briefcase, Plus, Send,
  BarChart3, Camera, MessageCircle, ClipboardList, AlertTriangle,
  Home, DollarSign, MapPin, Shield, Zap, Eye, Upload, ArrowRight, X,
  MoreHorizontal
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { GoldButton } from "../components/shared/UIComponents";
import { TermSheetComparison, DealTimeline, DocumentsTab, LenderMatchesTab } from "../components/dashboard";
import DealDetailModal from "../components/DealDetailModal";
import DealProgressStepper from "../components/DealProgressStepper";
import PhotoGallery from "../components/shared/PhotoGallery";
import EntityNotes from "../components/shared/EntityNotes";
import EntitlementBanner from "../components/shared/EntitlementBanner";
import DealPacketBuilder from "../components/DealPacketBuilder";
import EntityComms from "../components/shared/EntityComms";
import DealScenariosTab from "../components/DealScenariosTab";
import { STAGE_DESCRIPTIONS } from "../lib/stageConstants";
import { useTabNav } from "../hooks/useTabNav";
import PortfolioAnalytics from "../components/PortfolioAnalytics";
import { DealServicingPanel } from "../components/shared/ServicingPanel";
import ClosingChecklist from "../components/ClosingChecklist";
import { DealRiskScoreCard } from "../components/shared/ScoreCards";
import { useMyRequestCounts } from "../hooks/useCommCounts";
import usePullToRefresh from "../hooks/usePullToRefresh";
import { toast } from "sonner";
import { usePageNavigate } from "../hooks/usePageNavigate";

import ProfileEditor from "../components/shared/ProfileEditor";
import { fmtUSD, fmtUSDCompact } from "../utils/currency";
import { formatAddress } from "../utils/addressFormat";
import { MapThumbnail, MetricPill, StatusBadge } from "../components/shared/DesignSystem";
import { ActivityFeed } from "../components/shared/ActivityFeed";
import { DealProgressBar } from "../components/shared/DealProgressRing";
import { QuickActionsBar } from "../components/shared/QuickActions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt$ = (v) => v ? fmtUSD(v) : "--";

const LOAN_LABELS = {
  fix_flip: "Fix & Flip", bridge: "Bridge", dscr: "DSCR", cash_out: "Cash-Out Refi",
  construction: "Construction", ground_up: "Ground Up", multifamily: "Multifamily", commercial: "Commercial",
};
const PROP_LABELS = {
  sfr: "Single Family", single_family: "Single Family", multi: "Multi-Family", condo: "Condo",
  commercial: "Commercial", mixed_use: "Mixed Use", land: "Land",
};
const EXIT_LABELS = { flip: "Fix & Sell", rent: "Hold & Rent", refinance: "Refinance", sell: "Sell As-Is" };
const STAGE_LABELS = {
  submitted: "Submitted", in_review: "In Review", needs_info: "Info Requested", underwriting: "Underwriting",
  approved: "Approved", capital_matching: "Capital Matching", terms: "Terms Received", funding: "Funding",
  funded: "Funded", active: "Active", declined: "Declined", new_lead: "New",
  intake_complete: "Intake Done", docs_requested: "Docs Needed", packaging: "Packaging",
  matched: "Matched", submitted_to_lender: "Sent to Lenders", terms_received: "Terms Received",
  conditional_approval: "Conditional Approval", clear_to_close: "Clear to Close",
};
const STAGE_COLORS = {
  submitted: "bg-blue-100 text-blue-700 border-blue-200", in_review: "bg-amber-100 text-amber-700 border-amber-200",
  needs_info: "bg-orange-100 text-orange-700 border-orange-200", underwriting: "bg-purple-100 text-purple-700 border-purple-200",
  approved: "bg-emerald-100 text-emerald-700 border-emerald-200", capital_matching: "bg-indigo-100 text-indigo-700 border-indigo-200",
  terms: "bg-violet-100 text-violet-700 border-violet-200", funding: "bg-teal-100 text-teal-700 border-teal-200",
  funded: "bg-green-100 text-green-700 border-green-200", declined: "bg-red-100 text-red-700 border-red-200",
};
const STAGE_ORDER = ["submitted", "in_review", "underwriting", "approved", "capital_matching", "terms", "funding", "funded"];

const BORROWER_GUIDANCE = {
  submitted: { msg: "Your application is submitted. Our AI underwriting team is analyzing your deal.", icon: Clock, color: "border-amber-200 bg-amber-50 text-amber-800" },
  in_review: { msg: "We're reviewing your deal. You may receive requests for additional info.", icon: Clock, color: "border-blue-200 bg-blue-50 text-blue-800" },
  needs_info: { msg: "Additional information is needed. Please check open requests below.", icon: AlertTriangle, color: "border-orange-200 bg-orange-50 text-orange-800" },
  underwriting: { msg: "Your deal is in underwriting. Our team is evaluating terms and risk.", icon: BarChart3, color: "border-purple-200 bg-purple-50 text-purple-800" },
  approved: { msg: "Your deal is approved! We're now matching you with capital partners.", icon: CheckCircle, color: "border-emerald-200 bg-emerald-50 text-emerald-800" },
  capital_matching: { msg: "Lenders are reviewing your deal. Watch for interest and term sheets.", icon: Users, color: "border-indigo-200 bg-indigo-50 text-indigo-800" },
  terms: { msg: "Term sheets received. Review offers in the Offers tab.", icon: FileText, color: "border-violet-200 bg-violet-50 text-violet-800" },
  funding: { msg: "Your deal is in funding! Final closing steps are in progress.", icon: TrendingUp, color: "border-teal-200 bg-teal-50 text-teal-800" },
  funded: { msg: "Congratulations! Your deal has been funded.", icon: CheckCircle, color: "border-green-200 bg-green-50 text-green-800" },
};

export default function BorrowerDashboard() {
  const { user } = useAuth();
  const { navigateTo, navigateToEntity } = usePageNavigate();
  const { data, isLoading: loading, isError, refetch } = useQuery({
    queryKey: ["borrower-dashboard"],
    queryFn: () => axios.get(`${API}/dashboard/borrower`).then(r => r.data),
    staleTime: 30000,
    refetchOnWindowFocus: true,
  });
  const [expandedDeal, setExpandedDeal] = useState(null);
  const [activeTab, setActiveTab] = useTabNav("tab", "overview");
  const [detailLeadId, setDetailLeadId] = useState(null);
  const [dashSection, setDashSection] = useTabNav("section", "deals");
  const [dealNotifs, setDealNotifs] = useState({});
  const [allNotifs, setAllNotifs] = useState([]);
  const [dismissedNotifs, setDismissedNotifs] = useState(new Set());
  const [feedCollapsed, setFeedCollapsed] = useState(false);
  const { openCount: commOpenCount } = useMyRequestCounts();

  // Smart next-action prompt
  const { data: nextActionData } = useQuery({
    queryKey: ["borrower-next-action"],
    queryFn: () => axios.get(`${API}/borrower/next-action`).then(r => r.data),
    staleTime: 30000,
    refetchOnWindowFocus: true,
  });
  const [nextActionDismissed, setNextActionDismissed] = useState(false);

  const fetchNotifications = () => {
    axios.get(`${API}/notifications`).then(r => {
      const notifs = r.data?.notifications || r.data || [];
      setAllNotifs(notifs);
      const grouped = {};
      notifs.forEach(n => {
        if (n.lead_id && !n.read) {
          if (!grouped[n.lead_id]) grouped[n.lead_id] = [];
          grouped[n.lead_id].push(n);
        }
      });
      setDealNotifs(grouped);
    }).catch(() => {});
  };

  // Auto-expand first deal when data loads
  useEffect(() => {
    if (data?.applications?.length > 0 && !expandedDeal) {
      setExpandedDeal(data.applications[0].lead_id);
    }
  }, [data]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 20000);
    return () => clearInterval(interval);
  }, []);

  const refresh = () => refetch();

  const handleBadgeClick = (dealId, targetTab, notifs) => {
    // 1. Expand the deal card
    setExpandedDeal(dealId);
    // 2. Switch to the relevant tab
    setActiveTab(targetTab);
    // 3. Optimistically clear these notifications from local state
    setDealNotifs(prev => {
      const updated = { ...prev };
      const notifIds = new Set(notifs.map(n => n.notif_id || n.notification_id));
      if (updated[dealId]) {
        updated[dealId] = updated[dealId].filter(n => !notifIds.has(n.notif_id) && !notifIds.has(n.notification_id));
        if (updated[dealId].length === 0) delete updated[dealId];
      }
      return updated;
    });
    // 4. Mark notifications as read in background
    notifs.forEach(n =>
      axios.post(`${API}/notifications/${n.notif_id || n.notification_id}/read`).catch(() => {})
    );
  };

  const dismissFeedNotif = (notifId) => {
    setDismissedNotifs(prev => new Set(prev).add(notifId));
    axios.post(`${API}/notifications/${notifId}/read`).catch(() => {});
  };

  const { pullIndicator } = usePullToRefresh(refresh);

  const apps = data?.applications || [];
  const activeRequests = data?.active_requests || [];
  const tr = data?.track_record || {};

  // Build interleaved feed: deals + activity items sorted by time
  const feedItems = useMemo(() => {
    const items = [];
    // Add deal cards
    apps.forEach(deal => {
      items.push({ type: "deal", deal, ts: deal.created_at || deal.updated_at || "" });
    });
    // Add activity notifications as feed items
    allNotifs.slice(0, 20).forEach(n => {
      items.push({ type: "activity", notif: n, ts: n.created_at || "" });
    });
    // Sort by newest first
    items.sort((a, b) => (b.ts || "").localeCompare(a.ts || ""));
    return items;
  }, [apps, allNotifs]);

  const pipelineCount = apps.filter(a => !["declined", "funded", "active"].includes(a.stage)).length;
  const totalInterest = apps.reduce((s, a) => s + (a.lender_interest_count || 0), 0);
  const totalOffers = apps.reduce((s, a) => s + (a.term_sheet_count || 0), 0);

  if (loading) return (
    <div className="min-h-[60vh] bg-[#FBF8F1] flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>
  );

  if (isError) return (
    <div className="min-h-[60vh] bg-[#FBF8F1] flex items-center justify-center px-4" data-testid="borrower-dashboard-error">
      <div className="rounded-2xl border-2 border-red-300 bg-white p-8 max-w-md w-full text-center shadow-[0_4px_24px_rgba(220,38,38,0.08)]">
        <div className="mx-auto h-12 w-12 rounded-full bg-red-50 border border-red-200 flex items-center justify-center mb-4">
          <AlertTriangle className="h-6 w-6 text-red-500" />
        </div>
        <h3 className="text-lg font-bold text-[#0A0A0A] mb-2">Dashboard Unavailable</h3>
        <p className="text-sm text-[#666] mb-5">We're having trouble loading your dashboard. Please refresh the page or contact support.</p>
        <button onClick={() => refetch()} className="px-5 py-2.5 rounded-xl bg-emerald-700 text-white text-sm font-bold hover:bg-emerald-600 transition-colors" data-testid="dashboard-retry-btn">
          Try Again
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FBF8F1]" data-testid="borrower-dashboard">
      {pullIndicator}

      {/* ── BLOOMBERG TICKER BAR ─────────────────────────── */}
      <div className="bg-[#F5F0E8] border-b border-[#EDE8DC]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-6 py-2.5 overflow-x-auto scrollbar-hide">
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#065f46] whitespace-nowrap">Your Portfolio</span>
            <div className="h-3 w-px bg-[#D4CFC4]" />
            {[
              { label: "DEALS", value: tr.total_deals || apps.length },
              { label: "PIPELINE", value: pipelineCount },
              { label: "FUNDED", value: tr.funded_deals || 0, color: "text-emerald-700" },
              ...(tr.total_funded_volume ? [{ label: "VOLUME", value: fmt$(tr.total_funded_volume) }] : []),
              { label: "INTEREST", value: `${totalInterest}`, color: totalInterest > 0 ? "text-emerald-700" : "" },
              { label: "OFFERS", value: `${totalOffers}`, color: totalOffers > 0 ? "text-[#8B7340]" : "" },
              ...(tr.avg_days_to_fund ? [{ label: "AVG FUND", value: `${tr.avg_days_to_fund}d` }] : []),
            ].map((t, i) => (
              <div key={i} className="flex items-center gap-2 whitespace-nowrap">
                <span className="text-[9px] font-medium text-[#999] uppercase tracking-wider">{t.label}</span>
                <span className={`text-sm font-mono font-bold ${t.color || "text-[#0A0A0A]"}`}>{t.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── 3-COLUMN FEED LAYOUT ─────────────────────────── */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] xl:grid-cols-[260px_1fr_280px] gap-6">

          {/* ── LEFT SIDEBAR: Profile + Nav ── */}
          <div className="hidden lg:block">
            <div className="sticky top-6 space-y-4">
              {/* Profile Card */}
              <div className="rounded-2xl bg-white border border-[#EDE8DC] overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.03)]">
                <div className="h-16 bg-[#064e3b] relative">
                  <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(229,201,139,0.15),transparent_60%)]" />
                </div>
                <div className="px-4 pb-4 -mt-6 relative z-10">
                  <div className="w-12 h-12 rounded-full bg-emerald-600 border-2 border-white flex items-center justify-center text-white font-bold text-lg shadow-lg">
                    {(user?.full_name || "B")[0]}
                  </div>
                  <h3 className="text-sm font-bold text-[#0A0A0A] mt-2 tracking-tight">{user?.full_name}</h3>
                  <p className="text-[10px] text-[#888] mt-0.5">
                    {user?.approval_status === "approved" ? (
                      <span className="inline-flex items-center gap-1 text-emerald-600 font-medium">
                        <Shield className="h-3 w-3" /> Verified Borrower
                      </span>
                    ) : (
                      <span className="text-amber-600 font-medium">Pending Verification</span>
                    )}
                  </p>
                  <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-[#EDE8DC]">
                    <div className="text-center">
                      <div className="text-base font-bold text-[#0A0A0A] tabular-nums">{apps.length}</div>
                      <div className="text-[9px] text-[#999] uppercase tracking-wider">Deals</div>
                    </div>
                    <div className="text-center">
                      <div className="text-base font-bold text-emerald-600 tabular-nums">{tr.funded_deals || 0}</div>
                      <div className="text-[9px] text-[#999] uppercase tracking-wider">Funded</div>
                    </div>
                    <div className="text-center">
                      <div className="text-base font-bold text-[#0A0A0A] tabular-nums">{totalInterest}</div>
                      <div className="text-[9px] text-[#999] uppercase tracking-wider">Interest</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Nav */}
              <div className="rounded-2xl bg-white border border-[#EDE8DC] overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.03)]">
                <div className="p-1">
                  {[
                    { id: "deals", label: "Deal Feed", icon: Briefcase },
                    { id: "scenarios", label: "Compare Scenarios", icon: BarChart3 },
                    { id: "analytics", label: "Portfolio Analytics", icon: TrendingUp },
                    { id: "profile", label: "My Profile", icon: User },
                  ].map(s => (
                    <button key={s.id} onClick={() => setDashSection(s.id)}
                      data-testid={`dash-section-${s.id}`}
                      className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-xs font-semibold transition-all ${
                        dashSection === s.id ? "bg-emerald-700 text-white" : "text-[#666] hover:text-[#333] hover:bg-[#FDFCF9]"
                      }`}>
                      <s.icon className="h-3.5 w-3.5" />
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Submit Deal CTA */}
              <button onClick={() => navigateTo("apply")}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-emerald-700 text-white text-xs font-bold hover:bg-emerald-600 transition-colors shadow-lg shadow-emerald-900/20"
                data-testid="dashboard-apply-btn">
                <Plus className="h-4 w-4" /> Submit New Deal
              </button>
            </div>
          </div>

          {/* ── CENTER: The Feed ── */}
          <div className="min-w-0">
            {/* Mobile-only header */}
            <div className="lg:hidden mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-xl font-bold text-[#0A0A0A] tracking-tight">
                    Welcome, {user?.full_name?.split(" ")[0] || "Borrower"}
                  </h1>
                  <p className="text-xs text-[#888] mt-0.5">{apps.length} deal{apps.length !== 1 ? "s" : ""} in portfolio</p>
                </div>
                <button onClick={() => navigateTo("apply")}
                  className="flex items-center gap-1 px-3 py-2 rounded-full bg-emerald-700 text-white text-xs font-bold"
                  data-testid="mobile-apply-btn">
                  <Plus className="h-3.5 w-3.5" /> New Deal
                </button>
              </div>
              {/* Mobile section toggle */}
              <div className="flex gap-1 mt-3 overflow-x-auto scrollbar-hide" data-testid="dash-section-toggle">
                {[
                  { id: "deals", label: "Feed", icon: Briefcase },
                  { id: "scenarios", label: "Scenarios", icon: BarChart3 },
                  { id: "analytics", label: "Analytics", icon: TrendingUp },
                  { id: "profile", label: "Profile", icon: User },
                ].map(s => (
                  <button key={s.id} onClick={() => setDashSection(s.id)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                      dashSection === s.id ? "bg-emerald-700 text-white" : "text-[#888] hover:bg-[#FDFCF9] border border-[#EDE8DC]"
                    }`}>
                    <s.icon className="h-3 w-3" /> {s.label}
                  </button>
                ))}
              </div>
            </div>

            {dashSection === "scenarios" && <DealScenariosTab />}
            {dashSection === "analytics" && <PortfolioAnalytics />}
            {dashSection === "profile" && (() => { navigateTo("profile"); return null; })()}

            {/* ── SMART NEXT-ACTION BANNER ── */}
            {dashSection === "deals" && nextActionData?.has_action && !nextActionDismissed && (() => {
              const act = nextActionData.action;
              const colorMap = {
                orange: "border-orange-300 bg-orange-50 text-orange-800",
                amber: "border-amber-300 bg-amber-50 text-amber-800",
                blue: "border-blue-300 bg-blue-50 text-blue-800",
                purple: "border-purple-300 bg-purple-50 text-purple-800",
                gray: "border-gray-300 bg-gray-50 text-gray-800",
              };
              const bannerColor = colorMap[act.color] || colorMap.amber;
              const iconMap = {
                "alert-triangle": AlertTriangle,
                "upload": Upload,
                "camera": Camera,
                "file-text": FileText,
                "clipboard": ClipboardList,
                "clock": Clock,
              };
              const BannerIcon = iconMap[act.icon] || Zap;
              return (
                <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="mb-4" data-testid="next-action-banner">
                  <div className={`rounded-2xl border-2 overflow-hidden ${bannerColor}`}>
                    <div className="flex items-center gap-3 px-5 py-3.5">
                      <BannerIcon className="h-5 w-5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="text-xs font-bold">{act.message}</span>
                      </div>
                      <button
                        onClick={() => {
                          if (act.lead_id) {
                            setExpandedDeal(act.lead_id);
                            setActiveTab(act.cta_tab || "overview");
                          }
                        }}
                        className="shrink-0 px-3 py-1.5 rounded-lg bg-white/80 text-xs font-bold hover:bg-white transition-colors border border-current/20"
                        data-testid="next-action-cta"
                      >
                        {act.cta_label || "Take Action"} <ArrowRight className="h-3 w-3 inline ml-1" />
                      </button>
                      <button
                        onClick={() => setNextActionDismissed(true)}
                        className="shrink-0 p-1 rounded-full hover:bg-white/50 transition-colors"
                        data-testid="next-action-dismiss"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })()}

            {/* ── ACTION REQUIRED BANNER ── */}
            {dashSection === "deals" && activeRequests.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mb-4">
                <div className="rounded-2xl bg-white border border-amber-200 overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.03)]">
                  <div className="flex items-center gap-3 px-5 py-3 bg-amber-50/80 border-b border-amber-100">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    <span className="text-xs font-bold text-amber-800">Action Required</span>
                    <span className="text-[10px] text-amber-600 font-medium ml-auto">{activeRequests.length} pending</span>
                  </div>
                  <div className="p-3 space-y-1.5">
                    {activeRequests.slice(0, 3).map(req => (
                      <div key={req.request_id}
                        onClick={() => {
                          const dealId = req.lead_id || req.entity_id;
                          if (dealId) {
                            setExpandedDeal(dealId);
                            setActiveTab("overview");
                          }
                        }}
                        className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl hover:bg-[#FDFCF9] transition-colors cursor-pointer" data-testid={`action-item-${req.request_id}`}>
                        <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold border uppercase
                          ${req.priority === "critical" ? "bg-red-50 text-red-700 border-red-200" :
                            req.priority === "high" ? "bg-orange-50 text-orange-700 border-orange-200" :
                            "bg-amber-50 text-amber-700 border-amber-200"}`}>{req.priority}</span>
                        <span className="text-xs font-medium text-[#333] truncate flex-1">{req.title}</span>
                        <ChevronRight className="h-3.5 w-3.5 text-[#ccc]" />
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* EMPTY STATE */}
            {dashSection === "deals" && apps.length === 0 && (
              <div className="space-y-4">
                {!user?.onboarding_complete && (
                  <EntitlementBanner type="info" title="Welcome to Imaani Capital"
                    message="Submit your first financing scenario to get started. Our AI will underwrite and match you with qualified lenders." />
                )}
                <div className="rounded-2xl bg-white border border-[#EDE8DC] p-12 text-center shadow-[0_2px_20px_rgba(0,0,0,0.04)]">
                  <div className="mx-auto h-16 w-16 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-5">
                    <Building2 className="h-7 w-7 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0A0A0A] mb-2">No Capital Requests Yet</h3>
                  <p className="text-sm text-[#555] mb-6 max-w-md mx-auto">Submit your first financing scenario to receive AI-powered underwriting and lender matching.</p>
                  <GoldButton onClick={() => navigateTo("apply")} data-testid="dashboard-apply-first-btn">
                    Start Your Capital Request <ArrowRight className="h-4 w-4 inline ml-1" />
                  </GoldButton>
                </div>
              </div>
            )}

            {/* ── THE FEED: Deal Cards + Activity Items ── */}
            {dashSection === "deals" && apps.length > 0 && (
              <div className="space-y-4">
                {/* Activity Feed — collapsible scrollable box */}
                {feedItems.some(item => item.type === "activity") && (
                  <div className="rounded-2xl bg-white border border-[#EDE8DC] overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.03)]">
                    <button
                      onClick={() => setFeedCollapsed(!feedCollapsed)}
                      className="w-full flex items-center justify-between px-5 py-3 text-xs font-semibold text-[#555] hover:bg-[#FDFCF9] transition-all"
                      data-testid="feed-collapse-toggle"
                    >
                      <span className="flex items-center gap-2">
                        <Zap className="h-3.5 w-3.5 text-emerald-600" />
                        Activity Feed
                        <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold">
                          {feedItems.filter(item => item.type === "activity").length}
                        </span>
                      </span>
                      {feedCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                    </button>
                    {!feedCollapsed && (
                      <div className="max-h-[320px] overflow-y-auto border-t border-[#EDE8DC] divide-y divide-[#f0ebe0]" data-testid="activity-feed-scroll">
                        {feedItems.filter(item => item.type === "activity").map((item, i) => {
                          const n = item.notif;
                          const nid = n.notif_id || n.notification_id || `act-${i}`;
                          if (dismissedNotifs.has(nid)) return null;
                          const entityId = n.entity_id || n.lead_id || n.deal_id;
                          const entityType = n.entity_type || "deal";
                          const nType = n.type || "";

                          // Determine if this notification is actionable and where it should navigate
                          const isApproved = nType === "document_approved" || nType === "account_verified" || nType === "deal_funded";
                          const isRejected = nType === "document_rejected" || nType === "document_resubmit" || nType === "document_review";
                          const isUrgent = isRejected || nType === "document_request" || n.priority === "critical" || n.priority === "high";
                          const isDealRelated = entityType === "deal" || entityType === "lead" || nType.startsWith("deal_") || nType.startsWith("term_sheet") || nType.startsWith("lender_");

                          const handleFeedClick = () => {
                            if (isApproved) return; // Approved/verified items — no navigation needed
                            if (isRejected) {
                              // Rejected docs → go to profile documents page
                              window.history.pushState({}, "", "/profile?tab=documents");
                              navigateTo("profile");
                              return;
                            }
                            if (isDealRelated && entityId) {
                              // Deal-related → expand the deal card in dashboard
                              setExpandedDeal(entityId);
                              setDashSection("deals");
                              return;
                            }
                            // Fallback: don't navigate to random entity pages
                          };

                          return (
                            <div key={nid}
                              onClick={handleFeedClick}
                              className={`relative group/notif flex items-start gap-3 px-5 py-3 transition-all ${
                                isApproved ? "cursor-default" : "cursor-pointer hover:bg-[#FDFCF9]"
                              } ${isUrgent ? "bg-amber-50/60 border-l-2 border-l-amber-400" : ""}`}
                              data-testid={`feed-activity-${i}`}
                            >
                              <div className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                                n.type === "term_sheet_received" ? "bg-[#E5C98B]/20 text-[#8B7340]" :
                                n.type === "lender_interest" ? "bg-emerald-50 text-emerald-600" :
                                n.type === "deal_funded" ? "bg-green-50 text-green-600" :
                                n.type === "deal_stage_advanced" ? "bg-blue-50 text-blue-600" :
                                n.type === "document_review" || n.type === "document_resubmit" ? "bg-amber-50 text-amber-600" :
                                "bg-gray-50 text-gray-500"
                              }`}>
                                {n.type === "term_sheet_received" ? <FileText className="h-3.5 w-3.5" /> :
                                 n.type === "lender_interest" ? <Users className="h-3.5 w-3.5" /> :
                                 n.type === "deal_funded" ? <CheckCircle className="h-3.5 w-3.5" /> :
                                 n.type === "deal_stage_advanced" ? <TrendingUp className="h-3.5 w-3.5" /> :
                                 n.type === "document_review" || n.type === "document_resubmit" ? <FileText className="h-3.5 w-3.5" /> :
                                 <Zap className="h-3.5 w-3.5" />}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-xs font-medium text-[#333] leading-relaxed">{(n.message || n.title || "").replace(/[\w\s]+(Capital|Partners|Lending|Group|Fund|Financial|Holdings|Investments|LLC|Inc)\b/gi, "A qualified lender")}</p>
                                <p className="text-[10px] text-[#aaa] mt-1">{_relativeTime(n.created_at)}</p>
                              </div>
                              <div className="flex items-center gap-2 shrink-0 mt-1">
                                {isUrgent && <AlertTriangle className="h-3 w-3 text-amber-500" />}
                                {!n.read && <div className="w-2 h-2 rounded-full bg-emerald-500" />}
                                {!isApproved && (isRejected || isDealRelated) && <ChevronRight className="h-3.5 w-3.5 text-[#ccc] group-hover/notif:text-emerald-500 transition-colors" />}
                              </div>
                              <button
                                onClick={(e) => { e.stopPropagation(); dismissFeedNotif(nid); }}
                                className="absolute top-2 right-2 p-1 rounded-full bg-white/80 text-[#bbb] hover:text-[#666] hover:bg-gray-100 opacity-0 group-hover/notif:opacity-100 transition-opacity"
                                data-testid={`dismiss-notif-${i}`}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Deal Cards */}
                {feedItems.filter(item => item.type === "deal").map((item, i) => {
                  const deal = item.deal;
                  return (
                    <BorrowerDealCard
                      key={deal.lead_id}
                      deal={deal}
                      index={i}
                      onView360={() => navigateToEntity("deal", deal.lead_id)}
                    />
                  );
                })}
              </div>
            )}
          </div>

          {/* ── RIGHT SIDEBAR: Market Ticker ── */}
          <div className="hidden xl:block">
            <div className="sticky top-6 space-y-4">
              {/* Deal Progress Summary */}
              <div className="rounded-2xl bg-white border border-[#EDE8DC] overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.03)]">
                <div className="px-4 py-3 border-b border-[#EDE8DC]">
                  <h3 className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#888]">Pipeline Status</h3>
                </div>
                <div className="p-3 space-y-1.5">
                  {apps.map((deal) => {
                    const stageIdx = STAGE_ORDER.indexOf(deal.stage);
                    const pct = stageIdx >= 0 ? Math.round(((stageIdx + 1) / STAGE_ORDER.length) * 100) : 10;
                    return (
                      <button key={deal.lead_id}
                        onClick={() => { setDashSection("deals"); setExpandedDeal(deal.lead_id); }}
                        className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl hover:bg-[#FDFCF9] transition-colors text-left">
                        <div className="min-w-0 flex-1">
                          <div className="text-[10px] font-bold text-[#0A0A0A] truncate">{deal.deal_number}</div>
                          <div className="text-[9px] text-[#999]">{LOAN_LABELS[deal.loan_type] || deal.loan_type}</div>
                        </div>
                        <div className="w-16 flex items-center gap-1.5">
                          <div className="flex-1 h-1.5 rounded-full bg-[#EDE8DC] overflow-hidden">
                            <div className={`h-full rounded-full transition-all duration-500 ${deal.stage === "funded" ? "bg-emerald-500" : "bg-[#E5C98B]"}`} style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-[9px] font-mono font-bold text-[#888] tabular-nums">{pct}%</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="rounded-2xl bg-white border border-[#EDE8DC] overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.03)]">
                <div className="px-4 py-3 border-b border-[#EDE8DC]">
                  <h3 className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#888]">Quick Actions</h3>
                </div>
                <div className="p-2 space-y-1">
                  {[
                    { icon: Upload, label: "Upload Document", action: () => {
                      if (expandedDeal) { setActiveTab("documents"); }
                      else if (apps.length === 1) { setExpandedDeal(apps[0].lead_id); setActiveTab("documents"); }
                      else { navigateTo("profile"); }
                    }},
                    { icon: MessageCircle, label: "Message Team", action: () => {
                      if (expandedDeal) { setActiveTab("messages"); }
                      else if (apps.length === 1) { setExpandedDeal(apps[0].lead_id); setActiveTab("messages"); }
                      else { toast.info("Select a deal first to message the team"); }
                    }},
                    { icon: Clock, label: "Check Timeline", action: () => {
                      if (expandedDeal) { setActiveTab("timeline"); }
                      else if (apps.length === 1) { setExpandedDeal(apps[0].lead_id); setActiveTab("timeline"); }
                    }},
                    { icon: Eye, label: "View Deal 360", action: () => {
                      const dealId = expandedDeal || (apps.length === 1 ? apps[0].lead_id : null);
                      if (dealId) navigateToEntity("deal", dealId);
                    }},
                  ].map((a, i) => (
                    <button key={i} onClick={a.action}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-xs text-[#555] font-medium hover:bg-[#FDFCF9] hover:text-[#333] transition-colors">
                      <a.icon className="h-3.5 w-3.5 text-[#bbb]" /> {a.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Lender Activity Ticker */}
              {totalInterest > 0 && (
                <div className="rounded-2xl bg-white border border-[#EDE8DC] overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.03)]">
                  <div className="px-4 py-3 border-b border-[#EDE8DC]">
                    <h3 className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#888]">Lender Activity</h3>
                  </div>
                  <div className="p-3 space-y-2">
                    {apps.filter(d => d.lender_interest_count > 0).map(deal => (
                      <div key={deal.lead_id} className="flex items-center justify-between py-2 border-b border-[#EDE8DC] last:border-0">
                        <span className="text-[10px] font-medium text-[#333]">{deal.deal_number}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-mono text-emerald-600 font-bold">{deal.lender_interest_count} interested</span>
                          {deal.term_sheet_count > 0 && (
                            <span className="text-[10px] font-mono text-[#E5C98B] font-bold">{deal.term_sheet_count} offers</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {detailLeadId && <DealDetailModal leadId={detailLeadId} onClose={() => setDetailLeadId(null)} viewerRole="borrower" />}
    </div>
  );
}

function _relativeTime(ts) {
  if (!ts) return "";
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString();
}

/* ═══════════════════════════════════════════════════════════════
   Deal Card — Expandable
   ═══════════════════════════════════════════════════════════════ */

const DEAL_TABS = [
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "documents", label: "Documents", icon: FileText },
  { id: "offers", label: "Offers", icon: FileText },
  { id: "messages", label: "Messages", icon: MessageCircle },
];

const MORE_TABS = [
  { id: "closing", label: "Closing", icon: ClipboardList, fundingOnly: true },
  { id: "timeline", label: "Timeline", icon: Clock },
  { id: "photos", label: "Photos", icon: Camera },
  { id: "deal_package", label: "Deal Package", icon: Briefcase },
  { id: "lenders", label: "Lenders", icon: Users },
  { id: "servicing", label: "Servicing", icon: DollarSign, fundedOnly: true },
];

/* ═══ Stage Progress Dots ═══ */
const PROGRESS_STAGES = [
  { key: "submitted", label: "Submitted" },
  { key: "reviewing", label: "Reviewing" },
  { key: "approved", label: "Approved" },
  { key: "funded", label: "Funded" },
  { key: "servicing", label: "Servicing" },
];

function stageToProgressIndex(stage) {
  const map = {
    submitted: 0, new_lead: 0, intake_complete: 0,
    in_review: 1, needs_info: 1, underwriting: 1, docs_requested: 1, packaging: 1,
    approved: 2, capital_matching: 2, matched: 2, submitted_to_lender: 2, terms: 2, conditional_approval: 2, clear_to_close: 2, terms_received: 2, funding: 2,
    funded: 3, active: 4,
  };
  return map[stage] ?? 0;
}

function StageProgressDots({ stage }) {
  const activeIdx = stageToProgressIndex(stage);
  return (
    <div className="flex items-center gap-0 w-full" data-testid="stage-progress-dots">
      {PROGRESS_STAGES.map((s, i) => {
        const isDone = i < activeIdx;
        const isActive = i === activeIdx;
        const isPending = i > activeIdx;
        return (
          <React.Fragment key={s.key}>
            <div className="flex flex-col items-center" style={{ minWidth: 0 }}>
              <div
                style={{
                  width: 10, height: 10, borderRadius: "50%",
                  background: isDone ? "var(--ic-green-dark)" : isActive ? "var(--ic-gold)" : "var(--ic-border)",
                  transition: "background 0.3s ease",
                }}
                title={`${s.label}${isActive ? " (current)" : isDone ? " (complete)" : ""}`}
              />
              <span style={{
                fontSize: 9, marginTop: 3, color: isActive ? "var(--ic-gold)" : isDone ? "var(--ic-green-dark)" : "var(--ic-text-secondary)",
                fontWeight: isActive ? 700 : 500, whiteSpace: "nowrap",
              }}>{s.label}</span>
            </div>
            {i < PROGRESS_STAGES.length - 1 && (
              <div style={{ flex: 1, height: 1, background: "var(--ic-border)", margin: "0 4px", marginBottom: 14 }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function BorrowerDealCard({ deal, index, onView360 }) {
  const loanAmt = deal.requested_loan_amount || deal.purchase_price || 0;
  const ltvPct = deal.arv ? `${((loanAmt / deal.arv) * 100).toFixed(1)}%` : "N/A";
  const arvDisplay = deal.arv ? fmtUSDCompact(deal.arv) : "N/A";
  const loanDisplay = fmtUSDCompact(loanAmt);
  const formatted = formatAddress(deal.property_address || "");
  const parts = formatted.split(",").map(s => s.trim());
  const street = parts[0] || "Address Pending";
  const cityState = parts.length > 1 ? parts.slice(1).join(", ") : (deal.state || "");
  const displayStatus = STAGE_LABELS[deal.stage] || deal.stage || "Submitted";

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <div className="ic-deal-card" data-testid={`borrower-deal-card-${deal.lead_id}`}>
        {/* Map Thumbnail */}
        <div className="ic-deal-card-map" onClick={onView360}>
          <MapThumbnail address={deal.property_address} height={140} />
          <span className="ic-deal-card-map-overlay-lt">
            {LOAN_LABELS[deal.loan_type] || deal.loan_type}
          </span>
        </div>

        {/* Card Body */}
        <div className="ic-deal-card-body">
          <div className="ic-deal-card-address">{street}</div>
          <div className="ic-deal-card-city">{cityState}</div>
          <div className="ic-deal-card-id">{deal.deal_number || `Deal ${deal.lead_id?.substring(0, 8)}`}</div>

          <div className="ic-deal-card-pills">
            <MetricPill label="Loan" value={loanDisplay} highlight />
            <MetricPill label="LTV" value={ltvPct} />
            <MetricPill label="ARV" value={arvDisplay} />
          </div>

          <div className="ic-deal-card-context">
            {[
              LOAN_LABELS[deal.loan_type] || deal.loan_type,
              deal.property_type ? (PROP_LABELS[deal.property_type] || deal.property_type) : null,
              deal.lender_interest_count > 0 ? `${deal.lender_interest_count} lenders interested` : null,
              deal.term_sheet_count > 0 ? `${deal.term_sheet_count} offers` : null,
            ].filter(Boolean).join(" \u00B7 ")}
          </div>

          {/* Stage Progress Dots */}
          <div style={{ marginBottom: 14 }}>
            <StageProgressDots stage={deal.stage} />
          </div>

          {/* Status badge */}
          <div className="ic-deal-card-status">
            <StatusBadge status={displayStatus} />
          </div>

          {/* Single CTA Button */}
          <div className="ic-deal-card-buttons">
            <button className="ic-btn-primary" style={{ flex: "1 1 100%" }} onClick={onView360} data-testid={`view-deal-${deal.lead_id}`}>
              View My Deal <ArrowRight className="h-3.5 w-3.5 inline ml-1" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}



function DealCard({ deal, index, expanded, onToggle, activeTab, onTabChange, onView360, requests, onRefresh, commOpenCount, userData, unreadNotifs, onBadgeClick }) {
  const stage = deal.stage || "submitted";
  const stageColor = STAGE_COLORS[stage] || "bg-gray-100 text-gray-600 border-gray-200";
  const purchase = deal.purchase_price || 0;
  const rehab = deal.rehab_budget || 0;
  const totalCost = purchase + rehab;
  const requested = deal.requested_loan_amount || Math.round(totalCost * 0.85);
  const arv = deal.arv || 0;
  const ltc = totalCost ? ((requested / totalCost) * 100).toFixed(1) : null;
  const ltv = arv ? ((requested / arv) * 100).toFixed(1) : null;
  const stageIdx = STAGE_ORDER.indexOf(stage);
  const stageProgress = stageIdx >= 0 ? ((stageIdx + 1) / STAGE_ORDER.length) * 100 : 10;

  // Completeness / checklist
  const comp = deal.completeness || {};
  const docsNeeded = comp.docs_needed || [];
  const photoCount = comp.photo_count || 0;
  const photoMin = comp.photo_minimum || 5;
  const photosSufficient = comp.photos_sufficient || false;
  const readyForReview = comp.ready_for_review;
  const compPct = comp.pct || 0;

  // More dropdown state
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const visibleMoreTabs = MORE_TABS.filter(tab => {
    if (tab.fundedOnly && deal.stage !== "funded") return false;
    if (tab.fundingOnly && !["terms", "funding"].includes(deal.stage)) return false;
    return true;
  });
  const isMoreTabActive = visibleMoreTabs.some(tab => tab.id === activeTab);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="relative group rounded-2xl border border-[#EDE8DC] bg-white shadow-[0_2px_20px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_40px_rgba(6,95,70,0.08)] hover:border-emerald-200 transition-all overflow-hidden"
      data-testid={`deal-card-${index}`}
    >
      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-emerald-600 via-[#E5C98B] to-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-l-2xl" />
      {/* Card Header — Always Visible */}
      <div onClick={onToggle} className="w-full text-left p-5 hover:bg-[#faf7f2]/50 transition-colors cursor-pointer" role="button" tabIndex={0} data-testid={`deal-toggle-${deal.lead_id}`}>
        <div className="flex items-start gap-4">
          {/* Left: Deal info */}
          <div className="flex-1 min-w-0">
            {/* Row 1: Deal ID → Loan Type → Stage → Checklist (max 4 badges in collapsed view) */}
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-sm font-bold text-[#0A0A0A]" data-testid={`deal-number-${deal.lead_id}`}>{deal.deal_number}</span>
              <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-200" data-testid={`deal-loantype-${deal.lead_id}`}>
                {LOAN_LABELS[deal.loan_type] || deal.loan_type}
              </span>
              <span className={`px-2 py-0.5 rounded-lg text-[10px] font-bold border ${stageColor}`} data-testid={`deal-stage-${deal.lead_id}`}>
                {STAGE_LABELS[stage] || stage}
              </span>
              {/* Third badge: one warning badge (Docs Needed, Requests, or Complete) */}
              {readyForReview ? (
                <span className="px-2 py-0.5 rounded-lg text-[10px] font-bold bg-green-100 text-green-700 border border-green-200 flex items-center gap-1" data-testid={`deal-ready-${deal.lead_id}`}>
                  <CheckCircle className="h-3 w-3" /> Complete
                </span>
              ) : requests.length > 0 ? (
                <span className="px-2 py-0.5 rounded-lg text-[10px] font-bold bg-orange-100 text-orange-600 border border-orange-200 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> {requests.length} Request{requests.length > 1 ? "s" : ""}
                </span>
              ) : docsNeeded.length > 0 ? (
                <span className="px-2 py-0.5 rounded-lg text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200 flex items-center gap-1" data-testid={`deal-docs-needed-${deal.lead_id}`}>
                  <AlertTriangle className="h-3 w-3" /> {docsNeeded.length} Doc{docsNeeded.length > 1 ? "s" : ""} Needed
                </span>
              ) : !photosSufficient ? (
                <span className="px-2 py-0.5 rounded-lg text-[10px] font-bold bg-blue-100 text-blue-700 border border-blue-200 flex items-center gap-1" data-testid={`deal-photos-needed-${deal.lead_id}`}>
                  <Camera className="h-3 w-3" /> {photoCount}/{photoMin} Photos
                </span>
              ) : null}
              {/* Fourth badge: health score circle */}
              <DealRiskScoreCard leadId={deal.lead_id} compact />
              {/* Pulsing notification badges — updates badge uses teal */}
              {unreadNotifs.length > 0 && (() => {
                const termSheets = unreadNotifs.filter(n => n.title?.toLowerCase().includes("term sheet"));
                const messages = unreadNotifs.filter(n => n.title?.toLowerCase().includes("message"));
                const lenderActivity = unreadNotifs.filter(n => n.title?.toLowerCase().includes("lender") || n.title?.toLowerCase().includes("interest"));
                const other = unreadNotifs.filter(n => !termSheets.includes(n) && !messages.includes(n) && !lenderActivity.includes(n));
                return (
                  <>
                    {termSheets.length > 0 && (
                      <button onClick={(e) => { e.stopPropagation(); onBadgeClick("offers", termSheets); }}
                        className="relative px-2 py-0.5 rounded-lg text-[10px] font-bold bg-[#E5C98B]/20 text-[#8B7340] border border-[#E5C98B]/40 flex items-center gap-1 hover:bg-[#E5C98B]/30 transition-colors cursor-pointer" data-testid={`notif-term-sheet-${deal.lead_id}`}>
                        <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-[#E5C98B] animate-ping" />
                        <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-[#E5C98B]" />
                        <FileText className="h-3 w-3" /> New Term Sheet
                      </button>
                    )}
                    {messages.length > 0 && (
                      <button onClick={(e) => { e.stopPropagation(); onBadgeClick("messages", messages); }}
                        className="relative px-2 py-0.5 rounded-lg text-[10px] font-bold bg-blue-100 text-blue-700 border border-blue-200 flex items-center gap-1 hover:bg-blue-200 transition-colors cursor-pointer" data-testid={`notif-messages-${deal.lead_id}`}>
                        <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-blue-500 animate-ping" />
                        <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-blue-500" />
                        <MessageCircle className="h-3 w-3" /> {messages.length} New
                      </button>
                    )}
                    {lenderActivity.length > 0 && (
                      <button onClick={(e) => { e.stopPropagation(); onBadgeClick("lenders", lenderActivity); }}
                        className="relative px-2 py-0.5 rounded-lg text-[10px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-200 flex items-center gap-1 hover:bg-emerald-200 transition-colors cursor-pointer" data-testid={`notif-lender-${deal.lead_id}`}>
                        <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-emerald-500 animate-ping" />
                        <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-emerald-500" />
                        <Users className="h-3 w-3" /> Lender Activity
                      </button>
                    )}
                    {other.length > 0 && (
                      <button onClick={(e) => { e.stopPropagation(); onBadgeClick("overview", other); }}
                        className="relative px-2 py-0.5 rounded-lg text-[10px] font-bold bg-teal-100 text-teal-700 border border-teal-200 flex items-center gap-1 hover:bg-teal-200 transition-colors cursor-pointer" data-testid={`notif-other-${deal.lead_id}`}>
                        <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-teal-500 animate-ping" />
                        <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-teal-500" />
                        <Zap className="h-3 w-3" /> {other.length} Update{other.length > 1 ? "s" : ""}
                      </button>
                    )}
                  </>
                );
              })()}
            </div>
            <div className="flex items-center gap-3 text-xs text-[#666]">
              {deal.property_address && (
                <span className="flex items-center gap-1"><MapPin className="h-3 w-3 text-[#999]" /> {deal.property_address}</span>
              )}
              {deal.property_type && (
                <span className="text-[#999]">{PROP_LABELS[deal.property_type] || deal.property_type}</span>
              )}
            </div>
            {/* Animated Progress Bar */}
            <div className="mt-3">
              <DealProgressBar stage={stage} />
            </div>
          </div>

          {/* Right: Key metrics + expand */}
          <div className="flex items-center gap-4 shrink-0">
            <div className="hidden sm:flex items-center gap-4 text-right">
              <div>
                <div className="text-xs text-[#888]">Loan Amount</div>
                <div className="text-base font-bold text-[#0A0A0A]">{fmt$(requested)}</div>
              </div>
              {(deal.lender_interest_count > 0 || deal.term_sheet_count > 0) && (
                <div>
                  <div className="text-xs text-[#888]">Lender Interest</div>
                  <div className="text-base font-bold text-emerald-600">
                    {deal.lender_interest_count || 0}{deal.term_sheet_count > 0 && <span className="text-purple-600 ml-1">/ {deal.term_sheet_count} offers</span>}
                  </div>
                </div>
              )}
            </div>
            <div className={`transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}>
              <ChevronDown className="h-5 w-5 text-[#bbb]" />
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Detail */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="border-t border-[#EDE8DC]">
              {/* Tab Bar */}
              <div className="flex items-center gap-1 px-5 pt-3 pb-0" data-testid="deal-tab-bar">
                {DEAL_TABS.map(tab => (
                  <button key={tab.id} onClick={() => onTabChange(tab.id)}
                    data-testid={`deal-tab-${tab.id}`}
                    className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                      activeTab === tab.id
                        ? "bg-emerald-700 text-white shadow-sm"
                        : "text-[#888] hover:text-[#555] hover:bg-[#FDFCF9]"
                    }`}>
                    <tab.icon className="h-3.5 w-3.5" />
                    {tab.label}
                    {tab.id === "messages" && (commOpenCount > 0 || unreadNotifs.some(n => n.title?.toLowerCase().includes("message"))) && (
                      <span className="relative ml-1 px-1.5 py-0.5 rounded-full text-[8px] bg-red-500 text-white">
                        {commOpenCount || unreadNotifs.filter(n => n.title?.toLowerCase().includes("message")).length}
                        {unreadNotifs.some(n => n.title?.toLowerCase().includes("message")) && (
                          <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-red-500 animate-ping" />
                        )}
                      </span>
                    )}
                    {tab.id === "offers" && deal.term_sheet_count > 0 && (
                      <span className="ml-1 px-1.5 py-0.5 rounded-full text-[8px] bg-purple-500 text-white">{deal.term_sheet_count}</span>
                    )}
                  </button>
                ))}
                {/* More dropdown */}
                {visibleMoreTabs.length > 0 && (
                  <div ref={moreRef} className="relative">
                    <button onClick={() => setMoreOpen(!moreOpen)}
                      data-testid="deal-tab-more"
                      className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                        isMoreTabActive
                          ? "bg-emerald-700 text-white shadow-sm"
                          : "text-[#888] hover:text-[#555] hover:bg-[#FDFCF9]"
                      }`}>
                      <MoreHorizontal className="h-3.5 w-3.5" />
                      {isMoreTabActive ? visibleMoreTabs.find(t => t.id === activeTab)?.label : "More"}
                    </button>
                    {moreOpen && (
                      <div className="absolute top-full left-0 mt-1 z-30 bg-white rounded-xl border border-[#EDE8DC] shadow-[0_8px_30px_rgba(0,0,0,0.12)] py-1 min-w-[160px]">
                        {visibleMoreTabs.map(tab => (
                          <button key={tab.id} onClick={() => { onTabChange(tab.id); setMoreOpen(false); }}
                            data-testid={`deal-tab-${tab.id}`}
                            className={`w-full flex items-center gap-2 px-3.5 py-2.5 text-xs font-semibold transition-all ${
                              activeTab === tab.id
                                ? "bg-emerald-50 text-emerald-700"
                                : "text-[#666] hover:text-[#333] hover:bg-[#FDFCF9]"
                            }`}>
                            <tab.icon className="h-3.5 w-3.5" />
                            {tab.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div className="ml-auto shrink-0">
                  <button onClick={onView360}
                    className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-emerald-700 text-white text-xs font-medium hover:bg-emerald-600 transition-colors"
                    data-testid={`view-360-${deal.lead_id}`}>
                    <Eye className="h-3.5 w-3.5" /> View 360
                  </button>
                </div>
              </div>

              {/* Tab Content */}
              <div className="p-5">
                {activeTab === "overview" && (
                  <DealOverview deal={deal} requests={requests} onRefresh={onRefresh} onTabChange={onTabChange}
                    ltc={ltc} ltv={ltv} totalCost={totalCost} requested={requested} />
                )}
                {activeTab === "offers" && <TermSheetComparison leadId={deal.lead_id} />}
                {activeTab === "lenders" && <LenderMatchesTab leadId={deal.lead_id} />}
                {activeTab === "documents" && <DocumentsTab leadId={deal.lead_id} />}
                {activeTab === "deal_package" && <DealPacketBuilder dealId={deal.lead_id} isInternal={false} userRole="borrower" />}
                {activeTab === "photos" && <PhotoGallery leadId={deal.lead_id} canUpload={true} />}
                {activeTab === "timeline" && <DealTimeline leadId={deal.lead_id} />}
                {activeTab === "messages" && <EntityComms entityType="deal" entityId={deal.lead_id} />}
                {activeTab === "servicing" && deal.stage === "funded" && (
                  <BorrowerDealServicing leadId={deal.lead_id} />
                )}
                {activeTab === "closing" && ["terms", "funding"].includes(deal.stage) && (
                  <ClosingChecklist leadId={deal.lead_id} token={localStorage.getItem("ic_token")} userRole="borrower" dealNumber={deal.deal_number} />
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Deal Overview — Inside expanded card
   ═══════════════════════════════════════════════════════════════ */

function DealOverview({ deal, requests, onRefresh, onTabChange, ltc, ltv, totalCost, requested }) {
  const stage = deal.stage || "submitted";
  const g = BORROWER_GUIDANCE[stage] || BORROWER_GUIDANCE.submitted;
  const Icon = g.icon;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5" data-testid="deal-overview">
      {/* Stage Progress */}
      <DealProgressStepper stage={stage} dealNumber={deal.deal_number} />

      {/* Stage Transparency — what's happening and expected timeline */}
      {STAGE_DESCRIPTIONS[stage] && (
        <div className="rounded-xl border border-[#e8e0d0] bg-white p-4" data-testid="stage-transparency">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0">
              <Icon className="h-5 w-5 text-emerald-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="text-sm font-bold text-[#0A0A0A]">{STAGE_DESCRIPTIONS[stage].title}</h4>
                {STAGE_DESCRIPTIONS[stage].avgDays > 0 && (
                  <span className="px-2 py-0.5 rounded-md text-[9px] font-bold bg-[#f5f0e8] text-[#888] border border-[#EDE8DC]">
                    ~{STAGE_DESCRIPTIONS[stage].avgDays} day{STAGE_DESCRIPTIONS[stage].avgDays > 1 ? "s" : ""} avg
                  </span>
                )}
              </div>
              <p className="text-xs text-[#666] leading-relaxed mb-1.5">{STAGE_DESCRIPTIONS[stage].description}</p>
              <p className="text-[10px] text-emerald-700 font-medium flex items-center gap-1">
                <ArrowRight className="h-3 w-3" /> {STAGE_DESCRIPTIONS[stage].whatsNext}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stage guidance — only show for pre-approval stages to avoid duplicating the stage transparency banner above */}
      {!["approved", "capital_matching", "terms", "funding", "funded", "active"].includes(stage) && (
        <div className={`flex items-start gap-2.5 px-4 py-3 rounded-xl border text-xs ${g.color}`} data-testid="borrower-stage-guidance">
          <Icon className="h-4 w-4 shrink-0 mt-0.5" />
          <span>{g.msg}</span>
        </div>
      )}

      {/* Pre-Approval Estimate — shown after underwriting for stages >= approved */}
      {deal.arv > 0 && ["approved", "capital_matching", "terms", "funding", "funded", "active"].includes(stage) && (
        <div className="rounded-xl border-2 border-emerald-300 bg-gradient-to-r from-emerald-50 to-green-50 p-4" data-testid="pre-approval-badge">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="h-4 w-4 text-emerald-600" />
                <span className="text-xs font-bold text-emerald-800 uppercase tracking-wider">Pre-Approved</span>
              </div>
              <div className="text-2xl font-bold text-emerald-700">{fmt$(Math.round(deal.arv * 0.85))}</div>
              <div className="text-[10px] text-emerald-600 mt-0.5">Up to 85% of ARV ({fmt$(deal.arv)})</div>
            </div>
            <div className="text-right text-[10px] text-[#888]">
              <div>Estimated maximum</div>
              <div>Final terms from lender</div>
            </div>
          </div>
        </div>
      )}

      {/* Document & Photo Checklist Status */}
      {deal.completeness && !deal.completeness.ready_for_review && (
        <div className="space-y-3">
          {/* Documents Needed */}
          {(deal.completeness.docs_needed || []).length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="deal-checklist-alert">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <span className="text-xs font-bold text-amber-700">Documents Needed</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(deal.completeness.docs_needed || []).map((doc, i) => (
                  <span key={i} className="px-2 py-1 rounded-lg text-[10px] font-medium bg-white border border-amber-200 text-amber-700">
                    {doc}
                  </span>
                ))}
              </div>
              <button onClick={() => onTabChange("documents")}
                className="mt-3 text-xs font-semibold text-amber-700 hover:text-amber-800 underline" data-testid="upload-docs-link">
                Upload Documents
              </button>
            </div>
          )}
          {/* Photos Needed */}
          {!deal.completeness.photos_sufficient && (
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-4" data-testid="deal-photos-alert">
              <div className="flex items-center gap-2 mb-2">
                <Camera className="h-4 w-4 text-blue-600" />
                <span className="text-xs font-bold text-blue-700">Property Photos Needed</span>
                <span className="text-[10px] text-blue-500 ml-auto">{deal.completeness.photo_count || 0} / {deal.completeness.photo_minimum || 5} uploaded</span>
              </div>
              <p className="text-[10px] text-blue-600 mb-2">Upload at least {deal.completeness.photo_minimum || 5} property photos (exterior, interior, comparable properties)</p>
              <button onClick={() => onTabChange("photos")}
                className="text-xs font-semibold text-blue-700 hover:text-blue-800 underline" data-testid="upload-photos-link">
                Upload Photos
              </button>
            </div>
          )}
        </div>
      )}

      {/* Open Requests Alert */}
      {requests.length > 0 && (
        <div className="rounded-xl border border-orange-300 bg-orange-50 p-4" data-testid="active-requests-alert">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-4 w-4 text-orange-600" />
            <span className="text-xs font-bold text-orange-700">{requests.length} Open Request{requests.length !== 1 ? "s" : ""} Requiring Your Action</span>
          </div>
          <div className="space-y-2">
            {requests.slice(0, 3).map(req => (
              <div key={req.request_id} className="flex items-center justify-between text-xs bg-white rounded-lg px-3 py-2.5 border border-orange-200">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold border ${
                    req.priority === "critical" ? "bg-red-100 text-red-700 border-red-200" :
                    req.priority === "high" ? "bg-orange-100 text-orange-700 border-orange-200" :
                    "bg-amber-100 text-amber-700 border-amber-200"
                  }`}>{req.priority}</span>
                  <span className="font-medium text-[#222] truncate">{req.title}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Two-column layout: Property Snapshot + Deal Economics */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Property Snapshot */}
        <div className="rounded-xl border border-[#e8e0d0] bg-[#faf7f2] p-4">
          <h4 className="text-[10px] font-bold uppercase tracking-wider text-[#888] mb-3 flex items-center gap-1.5">
            <Building2 className="h-3 w-3" /> Property Snapshot
          </h4>
          <div className="space-y-2.5">
            {deal.property_address && (
              <div className="flex items-start gap-2">
                <MapPin className="h-3.5 w-3.5 text-[#999] mt-0.5 shrink-0" />
                <span className="text-sm font-medium text-[#0A0A0A]">{deal.property_address}</span>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 text-xs">
              {deal.property_type && <KV label="Type" value={PROP_LABELS[deal.property_type] || deal.property_type} />}
              {deal.state && <KV label="State" value={deal.state} />}
              {deal.bedrooms && <KV label="Bed / Bath" value={`${deal.bedrooms}bd / ${deal.bathrooms || "--"}ba`} />}
              {deal.sqft && <KV label="Sq Ft" value={Number(deal.sqft).toLocaleString()} />}
              {deal.year_built && <KV label="Year Built" value={deal.year_built} />}
            </div>
          </div>
        </div>

        {/* Deal Economics */}
        <div className="rounded-xl border border-[#e8e0d0] bg-[#faf7f2] p-4">
          <h4 className="text-[10px] font-bold uppercase tracking-wider text-[#888] mb-3 flex items-center gap-1.5">
            <DollarSign className="h-3 w-3" /> Deal Economics
          </h4>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <KV label="Purchase Price" value={fmt$(deal.purchase_price)} />
            {deal.rehab_budget > 0 && <KV label="Rehab Budget" value={fmt$(deal.rehab_budget)} />}
            {deal.arv > 0 && <KV label="ARV" value={fmt$(deal.arv)} />}
            {deal.rent_estimate_monthly > 0 && <KV label="Monthly Rent" value={fmt$(deal.rent_estimate_monthly)} />}
            <KV label="Total Cost" value={fmt$(totalCost)} />
            <KV label="Loan Amount" value={fmt$(requested)} highlight />
          </div>
          {/* LTC / LTV */}
          {(ltc || ltv) && (
            <div className="flex gap-2 mt-3 pt-3 border-t border-[#e8e0d0]">
              {ltc && (
                <div className="flex-1 rounded-lg bg-white border border-[#e8e0d0] p-2 text-center">
                  <div className="text-[9px] font-bold uppercase text-[#888]">LTC</div>
                  <div className="text-base font-bold text-[#0A0A0A]">{ltc}%</div>
                </div>
              )}
              {ltv && (
                <div className="flex-1 rounded-lg bg-white border border-[#e8e0d0] p-2 text-center">
                  <div className="text-[9px] font-bold uppercase text-[#888]">LTV</div>
                  <div className="text-base font-bold text-[#0A0A0A]">{ltv}%</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Status + Lender Activity Row */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Borrower Profile + Strategy */}
        <div className="rounded-xl border border-[#e8e0d0] bg-[#faf7f2] p-4">
          <h4 className="text-[10px] font-bold uppercase tracking-wider text-[#888] mb-3 flex items-center gap-1.5">
            <Shield className="h-3 w-3" /> Borrower Profile & Strategy
          </h4>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {deal.fico_estimate && <KV label="FICO Estimate" value={deal.fico_estimate} />}
            {deal.experience_projects != null && <KV label="Experience" value={`${deal.experience_projects} projects`} />}
            {deal.entity_name && <KV label="Entity" value={deal.entity_name} />}
            {deal.exit_strategy && <KV label="Exit Strategy" value={EXIT_LABELS[deal.exit_strategy] || deal.exit_strategy} />}
            {deal.closing_timeline_days && <KV label="Closing Timeline" value={`${deal.closing_timeline_days} days`} />}
          </div>
        </div>

        {/* Lender Activity */}
        <div className="rounded-xl border border-[#e8e0d0] bg-[#faf7f2] p-4">
          <h4 className="text-[10px] font-bold uppercase tracking-wider text-[#888] mb-3 flex items-center gap-1.5">
            <Users className="h-3 w-3" /> Lender Activity & Review
          </h4>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg bg-white border border-[#e8e0d0] p-3 text-center" data-testid={`lender-views-${deal.lead_id}`}>
              <div className="text-lg font-bold text-[#0A0A0A]">{deal.lender_views || 0}</div>
              <div className="text-[9px] text-[#888] uppercase font-bold">Reviewing</div>
            </div>
            <div className="rounded-lg bg-white border border-[#e8e0d0] p-3 text-center">
              <div className="text-lg font-bold text-emerald-600">{deal.lender_interest_count || 0}</div>
              <div className="text-[9px] text-[#888] uppercase font-bold">Interested</div>
            </div>
            <div className="rounded-lg bg-white border border-[#e8e0d0] p-3 text-center">
              <div className="text-lg font-bold text-purple-600">{deal.term_sheet_count || 0}</div>
              <div className="text-[9px] text-[#888] uppercase font-bold">Offers</div>
            </div>
            <div className="rounded-lg bg-white border border-[#e8e0d0] p-3 text-center">
              <div className={`text-sm font-bold ${deal.summary?.decision === "prequalify" ? "text-emerald-600" : "text-amber-600"}`}>
                {deal.summary?.decision === "prequalify" ? "Pre-Qualified" : deal.summary?.decision === "manual_review" ? "Manual Review" : "Under Review"}
              </div>
              <div className="text-[9px] text-[#888] uppercase font-bold">Lender Review</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Term Sheet Quick Compare (Top 3) ── */}
      {deal.term_sheet_count > 0 && (
        <TermSheetQuickCompare leadId={deal.lead_id} onTabChange={onTabChange} />
      )}

      {/* Completeness */}
      {deal.completeness?.pct != null && deal.completeness.pct < 100 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4" data-testid="deal-completeness">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-amber-600" />
              <span className="text-xs font-bold text-[#0A0A0A]">Deal Completeness</span>
            </div>
            <span className="text-sm font-bold text-amber-700">{deal.completeness.pct}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-amber-200 overflow-hidden">
            <div className="h-full rounded-full bg-amber-500 transition-all duration-500" style={{ width: `${deal.completeness.pct}%` }} />
          </div>
          <p className="text-[13px] leading-relaxed text-[#888] mt-2">Complete deals are prioritized for lender distribution. Upload documents and fill in missing information to improve your match quality.</p>
        </div>
      )}
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Term Sheet Quick Compare — Top 3 side-by-side
   ═══════════════════════════════════════════════════════════════ */

function TermSheetQuickCompare({ leadId, onTabChange }) {
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("ic_token");
    axios.get(`${API}/term-sheets/${leadId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const all = (r.data.term_sheets || []).filter(ts => ts.status !== "passed" && ts.status !== "withdrawn");
        setSheets(all.slice(0, 3));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [leadId]);

  if (loading || sheets.length === 0) return null;

  const bestRate = Math.min(...sheets.filter(s => s.interest_rate).map(s => s.interest_rate));
  const bestLTV = Math.max(...sheets.filter(s => s.ltv).map(s => Number(s.ltv)));

  // Badge logic: "Best Rate" for lowest rate, "Recommended" for best combination
  const scored = sheets.map(ts => {
    let score = 0;
    if (ts.interest_rate === bestRate) score += 2;
    if (ts.interest_rate && ts.interest_rate <= bestRate * 1.05) score += 1;
    if (ts.loan_amount && ts.loan_amount >= Math.max(...sheets.map(s => s.loan_amount || 0)) * 0.95) score += 1;
    if (ts.points != null && ts.points <= Math.min(...sheets.filter(s => s.points != null).map(s => Number(s.points))) * 1.1) score += 1;
    return { ...ts, _score: score };
  });
  const topScore = Math.max(...scored.map(s => s._score));

  return (
    <div className="rounded-xl border border-[#e8e0d0] bg-white overflow-hidden" data-testid="term-sheet-quick-compare">
      <div className="px-4 py-3 bg-[#faf7f2] border-b border-[#e8e0d0] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-purple-600" />
          <span className="text-xs font-bold text-[#0A0A0A]">Compare Offers</span>
          <span className="px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 text-[9px] font-bold">{sheets.length}</span>
        </div>
        <button onClick={() => onTabChange("offers")} className="text-[10px] font-semibold text-emerald-700 hover:text-emerald-600" data-testid="view-all-offers-link">
          View All <ArrowRight className="h-3 w-3 inline ml-0.5" />
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-[#f0ebe0]">
        {scored.map((ts, i) => {
          const isBestRate = ts.interest_rate === bestRate && sheets.length > 1;
          const isRecommended = ts._score === topScore && sheets.length > 1;
          return (
            <div key={ts.ts_id || i} className="p-4 relative" data-testid={`quick-compare-card-${i}`}>
              {/* Badges */}
              <div className="flex items-center gap-1.5 mb-3 min-h-[20px]">
                {isBestRate && (
                  <span className="px-2 py-0.5 rounded-md text-[8px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-200" data-testid={`badge-best-rate-${i}`}>
                    Best Rate
                  </span>
                )}
                {isRecommended && !isBestRate && (
                  <span className="px-2 py-0.5 rounded-md text-[8px] font-bold bg-[#E5C98B]/20 text-[#8B7340] border border-[#E5C98B]/40" data-testid={`badge-recommended-${i}`}>
                    Recommended
                  </span>
                )}
              </div>
              {/* Anonymized Lender */}
              <div className="text-[10px] text-[#999] uppercase font-bold tracking-wider mb-2">
                Lender {String.fromCharCode(65 + i)}
              </div>
              {/* 5 Key Fields */}
              <div className="space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-[#888]">Rate</span>
                  <span className={`text-sm font-bold ${isBestRate ? "text-emerald-600" : "text-[#0A0A0A]"}`}>
                    {ts.interest_rate ? `${ts.interest_rate}%` : "--"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-[#888]">LTV</span>
                  <span className="text-sm font-bold text-[#0A0A0A]">
                    {ts.ltv ? `${(Number(ts.ltv) * 100).toFixed(0)}%` : "--"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-[#888]">Term</span>
                  <span className="text-sm font-bold text-[#0A0A0A]">
                    {ts.term_months ? `${ts.term_months} mo` : "--"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-[#888]">Amount</span>
                  <span className="text-sm font-bold text-[#0A0A0A]">
                    {ts.loan_amount ? fmt$(ts.loan_amount) : "--"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-[#888]">Est. Monthly</span>
                  <span className="text-sm font-bold text-[#0A0A0A]">
                    {ts.interest_rate && ts.loan_amount
                      ? fmt$(Math.round((ts.loan_amount * (ts.interest_rate / 100)) / 12))
                      : "--"}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}



function KV({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[10px] text-[#999] uppercase font-bold">{label}</div>
      <div className={`mt-0.5 font-semibold truncate ${highlight ? "text-emerald-700" : "text-[#0A0A0A]"}`}>{value || "--"}</div>
    </div>
  );
}

function BorrowerRequestsPanel({ requests, dealId, onRefresh }) {
  const [respondingTo, setRespondingTo] = useState(null);
  const [responseText, setResponseText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleRespond = async (requestId) => {
    if (!responseText.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/comm/requests/${requestId}/respond`, { response_text: responseText.trim() });
      setRespondingTo(null);
      setResponseText("");
      onRefresh();
    } catch {} finally { setSubmitting(false); }
  };

  if (!requests.length) {
    return (
      <div className="text-center py-12 text-sm text-[#888]" data-testid="no-requests">
        <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
        <p className="font-medium">No open requests</p>
        <p className="text-xs mt-1">You're all caught up!</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="borrower-requests-panel">
      <div className="text-xs font-bold text-[#888] uppercase tracking-wider">Open Requests ({requests.length})</div>
      {requests.map(req => (
        <div key={req.request_id} className="border border-[#e5e5e5] rounded-xl bg-white overflow-hidden" data-testid={`request-card-${req.request_id}`}>
          <div className="p-4 border-b border-[#f0f0f0]">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold border ${
                  req.priority === "critical" ? "bg-red-100 text-red-700 border-red-200" :
                  req.priority === "high" ? "bg-orange-100 text-orange-700 border-orange-200" :
                  "bg-amber-100 text-amber-700 border-amber-200"
                }`}>{req.priority}</span>
              </div>
              {req.due_date && <span className="text-[10px] text-[#999]">Due: {new Date(req.due_date).toLocaleDateString()}</span>}
            </div>
            <h4 className="text-sm font-bold text-[#222]">{req.title}</h4>
            {req.description && <p className="text-xs text-[#666] mt-1">{req.description}</p>}
          </div>
          {respondingTo === req.request_id ? (
            <div className="p-3 bg-[#fafafa]">
              <textarea className="w-full border border-[#ddd] rounded-lg p-2.5 text-xs resize-none focus:outline-none focus:ring-1 focus:ring-emerald-500/50"
                rows={3} placeholder="Type your response..." value={responseText} onChange={e => setResponseText(e.target.value)} />
              <div className="flex gap-2 mt-2">
                <button onClick={() => handleRespond(req.request_id)} disabled={submitting || !responseText.trim()}
                  className="px-3 py-1.5 rounded-lg text-xs font-bold bg-emerald-700 text-white hover:bg-emerald-600 disabled:opacity-50">
                  {submitting ? "Sending..." : "Send Response"}
                </button>
                <button onClick={() => { setRespondingTo(null); setResponseText(""); }}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium text-[#666] hover:bg-[#eee]">Cancel</button>
              </div>
            </div>
          ) : (
            <div className="p-3 flex justify-end">
              <button onClick={() => setRespondingTo(req.request_id)}
                className="px-3 py-1.5 rounded-lg text-xs font-bold bg-emerald-700 text-white hover:bg-emerald-600"
                data-testid={`respond-btn-${req.request_id}`}>Respond</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}


function BorrowerDealServicing({ leadId }) {
  const [servicing, setServicing] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/servicing/deal/${leadId}`)
      .then(r => setServicing(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [leadId]);
  if (loading) return <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-emerald-600" /></div>;
  if (!servicing || servicing.status === "not_funded") return <div className="text-center py-8 text-sm text-[#888]">Servicing data available after funding.</div>;
  return <DealServicingPanel servicing={servicing} />;
}
